/**
 * Set a new password, reached from the emailed link (/#reset=…). One field,
 * show/hide, and a plain statement of what happens: this signs the driver in
 * here and out everywhere else.
 */
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { api, ApiError } from './api.js';
import { Button, InlineWarn } from './ui.js';

export function ResetScreen({ token, onDone, onCancel }: {
  token: string;
  onDone: () => void;
  /** Back to sign-in, e.g. to ask for a fresh link. */
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dead, setDead] = useState(false);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.resetPassword(token, password);
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.code === 'invalid_token') {
        setDead(true);
        setError('This link has expired or was already used. Ask for a new one from the sign-in screen.');
      } else if (err instanceof ApiError && err.status === 400) setError('Use a password of 8 or more characters.');
      else if (err instanceof ApiError && err.status === 429) setError('Too many attempts. Wait a few minutes and try again.');
      else setError('Couldn’t reach Surge — check your signal.');
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <h1 className="title" style={{ marginTop: 24 }}>Set a new password</h1>
      <p className="sub">You’ll be signed in on this phone and signed out everywhere else.</p>

      <div className="field">
        <label htmlFor="new-password">New password (8+ characters)</label>
        <div className="input-wrap">
          <input id="new-password" className="has-trailing" type={show ? 'text' : 'password'}
            autoComplete="new-password" value={password} disabled={dead}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && password.length >= 8 && !dead) void submit(); }} />
          <button type="button" className="trailing" aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show} onClick={() => setShow((s) => !s)}>
            {show ? <EyeOff size={22} aria-hidden="true" /> : <Eye size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {error && <InlineWarn danger>{error}</InlineWarn>}

      {!dead && (
        <Button variant="primary" busy={busy} disabled={password.length < 8} onClick={() => void submit()}>
          Save and sign in
        </Button>
      )}
      <Button variant={dead ? 'primary' : 'text'} onClick={onCancel}>
        {dead ? 'Back to sign in' : 'Cancel'}
      </Button>
    </div>
  );
}
